'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  BarChart3,
  AlertTriangle,
  Compass,
  Sparkles,
  Heart,
  Users,
  TrendingUp,
  Target,
  LineChart,
  Layout,
  Check,
} from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */
function CompareBarsBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <line x1="60" y1="360" x2="340" y2="360" stroke={tint.accent} strokeOpacity="0.35" strokeWidth="1.2" />
      {[
        { x: 80,  h: 70 },
        { x: 150, h: 110, hi: true },
        { x: 220, h: 55 },
        { x: 290, h: 85 },
      ].map((b, i) => (
        <g key={i}>
          <rect
            x={b.x}
            y={360 - b.h}
            width="48"
            height={b.h}
            rx="6"
            fill={tint.accent}
            fillOpacity={b.hi ? 0.85 : 0.35}
          />
          {b.hi && (
            <rect
              x={b.x - 4}
              y={360 - b.h - 4}
              width="56"
              height={b.h + 8}
              rx="8"
              fill="none"
              stroke={tint.accent}
              strokeOpacity="0.7"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
          )}
        </g>
      ))}
    </svg>
  );
}

function GapDeltaBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} aria-hidden="true">
      <rect x="60" y="300" width="280" height="22" rx="11" fill={tint.accent} fillOpacity="0.75" />
      <rect x="60" y="334" width="180" height="22" rx="11" fill={tint.accent} fillOpacity="0.30" />
      <line x1="240" y1="346" x2="240" y2="368" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.2" strokeDasharray="3 3" />
      <line x1="340" y1="312" x2="340" y2="368" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.2" strokeDasharray="3 3" />
      <line x1="240" y1="368" x2="340" y2="368" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="1.5" />
      <path d="M 245 365 L 240 368 L 245 371" fill="none" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M 335 365 L 340 368 L 335 371" fill="none" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function PerformanceLineBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <defs>
        <linearGradient id={`pl-area-${tint.accent.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.4" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="40" y1="360" x2="360" y2="360" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      <path
        d="M 40 340 L 100 320 L 160 330 L 220 295 L 280 270 L 340 305 L 340 360 L 40 360 Z"
        fill={`url(#pl-area-${tint.accent.replace('#', '')})`}
      />
      <path
        d="M 40 340 L 100 320 L 160 330 L 220 295 L 280 270 L 340 305"
        fill="none"
        stroke={tint.accent}
        strokeOpacity="0.8"
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="280" cy="270" r="5" fill={tint.accent} />
      <circle cx="280" cy="270" r="10" fill="none" stroke={tint.accent} strokeOpacity="0.35" strokeWidth="1.2" />
    </svg>
  );
}

function SentimentBarBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <g>
        <rect x="60"  y="320" width="160" height="22" rx="11" fill={tint.accent} fillOpacity="0.85" />
        <rect x="222" y="320" width="80"  height="22" rx="0"  fill={tint.accent} fillOpacity="0.45" />
        <rect x="304" y="320" width="40"  height="22" rx="11" fill={tint.accent} fillOpacity="0.20" />
      </g>
      {[60, 200, 340].map((x, i) => (
        <circle key={i} cx={x} cy="358" r="3" fill={tint.accent} fillOpacity="0.45" />
      ))}
      <line x1="60" y1="358" x2="340" y2="358" stroke={tint.accent} strokeOpacity="0.20" strokeWidth="1" />
    </svg>
  );
}

function DashboardTilesBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} aria-hidden="true">
      {[
        { x: 50,  bars: [12, 22, 16, 28] },
        { x: 155, bars: [18, 14, 26, 22] },
        { x: 260, bars: [10, 20, 30, 24] },
      ].map((tile, i) => (
        <g key={i}>
          <rect x={tile.x} y="280" width="90" height="80" rx="10" fill="#fff" stroke={tint.accent} strokeOpacity="0.45" />
          <rect x={tile.x + 12} y="292" width="40" height="5" rx="2" fill={tint.accent} fillOpacity="0.30" />
          <rect x={tile.x + 12} y="302" width="56" height="9" rx="2" fill={tint.accent} fillOpacity="0.70" />
          {tile.bars.map((h, j) => (
            <rect
              key={j}
              x={tile.x + 12 + j * 18}
              y={350 - h}
              width="10"
              height={h}
              rx="2"
              fill={tint.accent}
              fillOpacity="0.55"
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Competitive Benchmarking',
  tagline: 'Outperform the Market by Knowing It Better',
  body: [
    'Be the first to sense what’s shifting, uncover what others overlook, and move with clarity before momentum builds elsewhere. The brands that lead don’t follow signals — they decode them.',
    'Locobuzz turns competitive noise into sharp insight, giving you the foresight to see what’s next and the conviction to move first.',
  ],
};

/* DRAFT — review */
const IMPACT = [
  { value: '5,000+', label: 'Brands tracked across categories daily' },
  { value: '98%',    label: 'Accuracy on share-of-voice attribution' },
  { value: '2×',     label: 'Faster competitive response time on average' },
  { value: '360°',   label: 'Market view across every major platform' },
];

const STACK_POINTS = [
  { Icon: BarChart3,     title: 'Performance Tracking',  text: 'Track brand and competitor performance across engagement, sentiment, and visibility.', tint: 'blue' },
  { Icon: AlertTriangle, title: 'Threat Detection',       text: 'Catch emerging threats, sudden spikes, and narrative shifts before they go mainstream.', tint: 'violet' },
  { Icon: Compass,       title: 'Whitespace Discovery',   text: 'Uncover gaps and find whitespaces where your brand can dominate.',                       tint: 'sky' },
  { Icon: Sparkles,      title: 'Campaign Intelligence',  text: 'Identify high-performing campaigns and creatives across your category in real time.',   tint: 'blue' },
  { Icon: Heart,         title: 'Product Perception',     text: 'Understand what customers love or reject across competing products and experiences.',    tint: 'violet' },
  { Icon: Users,         title: 'One Unified View',       text: 'Align marketing, CX, and leadership teams with one unified view of the market.',         tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Competitive Landscape',
    title: 'Monitor the Competitive Landscape',
    lead: 'Capture and compare conversations across your industry, every single day.',
    bullets: [
      'Track mentions for your brand, products, and key competitors from all major platforms',
      'Identify volume spikes, sentiment shifts, and trending topics in real time',
      'Monitor how customer reactions vary across geographies and audiences',
      'Detect crisis signals early, whether they’re yours or theirs',
      'Stay on top of what the market is talking about before it hits headlines',
    ],
    cta: { label: 'Start Monitoring', href: '#book-demo' },
    visual: { Icon: BarChart3, title: 'Share of Voice', subtitle: 'Your brand vs. category', Bg: CompareBarsBg, tint: 'blue' },
  },
  {
    eyebrow: 'Drivers of the Gap',
    title: 'Decode What’s Driving the Gap',
    lead: 'Uncover why certain brands lead and where you can catch up or overtake.',
    bullets: [
      'Understand and act on the emotional tone and subtle innuendos of customers',
      'Surface themes in competitor praise or complaints using QRIC',
      'Understand what’s winning attention: influencers, campaigns, features, or price',
      'Benchmark across content, messaging, service quality, and social share of voice',
      'Group competitor feedback into themes to identify opportunities and threats',
    ],
    visual: { Icon: TrendingUp, title: 'Gap Analysis', subtitle: 'Where leaders pull ahead', Bg: GapDeltaBg, tint: 'violet' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'Campaign Effectiveness',
    title: 'Compare Campaign & Content Effectiveness',
    lead: 'Dissect what’s landing in your category and use it to sharpen your own strategy.',
    bullets: [
      'Spot which creatives, formats, and narratives are driving engagement',
      'Map campaign spikes to influencer mentions or real-world events',
      'See how your promotions perform against competitors in real time',
      'Identify which content builds love and which falls flat',
      'Fuel your own marketing calendar with tested, data-backed insights',
    ],
    cta: { label: 'Compare Campaigns', href: '#book-demo' },
    visual: { Icon: LineChart, title: 'Campaign Performance', subtitle: 'Spot the peaks worth copying', Bg: PerformanceLineBg, tint: 'sky' },
  },
  {
    eyebrow: 'Category Sentiment',
    title: 'Track Category Sentiment & Brand Perception',
    lead: 'Don’t just know where you stand — find out the why.',
    bullets: [
      'Map overall sentiment across competitors to detect shifting market moods',
      'Analyze loyalty, intent, and churn signals across the entire category',
      'Understand how pricing, product, or service changes are being received',
      'Get ahead of reputational threats and turn them into growth opportunities',
    ],
    visual: { Icon: Target, title: 'Sentiment Mix', subtitle: 'Positive · Neutral · Negative', Bg: SentimentBarBg, tint: 'blue' },
    reverse: true,
  },
  {
    eyebrow: 'Strategy & Alignment',
    title: 'Turn Insight Into Strategy',
    lead: 'Put insights in front of the people who move the brand forward.',
    bullets: [
      'Build live benchmarking dashboards for leadership',
      'Benchmark brand performance in live boardroom views',
      'Share custom reports across teams with a click',
      'Align marketing, CX, product, and leadership on a single source of competitive truth',
      'Make faster, bolder decisions backed by AI-powered intelligence',
    ],
    cta: { label: 'Book a Demo', href: '#book-demo' },
    visual: { Icon: Layout, title: 'Boardroom Dashboards', subtitle: 'One source of competitive truth', Bg: DashboardTilesBg, tint: 'violet' },
  },
];

/* DRAFT — review */
const FAQS = [
  { q: 'What is competitive benchmarking in Locobuzz?', a: 'Competitive benchmarking in Locobuzz lets you continuously compare your brand’s share of voice, sentiment, campaign performance, and audience engagement against competitors — all in real time, across every major digital channel.' },
  { q: 'Which competitors and platforms can I track?', a: 'You can track any brand or product keyword across 30+ digital sources including Instagram, X, Reddit, YouTube, news outlets, forums, and app stores. There is no limit on the number of competitors you can monitor.' },
  { q: 'How does share-of-voice measurement work?', a: 'Locobuzz aggregates all mentions of your brand and your defined competitors, then calculates the percentage of total conversations each brand owns in your category — updated continuously, not just in weekly reports.' },
  { q: 'Can I receive alerts when a competitor makes a sudden move?', a: 'Yes. SignalSense™ detects abnormal spikes in competitor volume, sentiment shifts, or viral momentum and delivers instant alerts so your team can respond before a competitor narrative takes hold.' },
  { q: 'How quickly can my team get started?', a: 'Most teams go live quickly with guided onboarding. Book a demo and a specialist will tailor a rollout to your brand, category, and markets.' },
];

/* DRAFT — review */
const PANEL = {
  title: 'Competitor Watch',
  badge: 'Share of Voice',
  kpiValue: 34,
  kpiLabel: '% Share of Voice — your brand',
  trend: [18, 22, 19, 26, 24, 30, 28, 32, 36, 34],
  trendDelta: '+6 pts',
  segments: [
    { label: 'Your Brand', pct: 34, tone: 'brand' },
    { label: 'Competitor A', pct: 28, tone: 'neu' },
    { label: 'Competitor B', pct: 38, tone: 'neg' },
  ],
  feedLabel: 'Competitor signals',
  feed: [
    { text: 'Competitor A launched a flash sale — #deals trending', meta: 'X · trending', tag: 'X', tone: 'neg' },
    { text: 'New product review for Brand B going viral on Reddit', meta: 'r/gadgets', tag: 'Reddit', tone: 'neg' },
    { text: 'Your campaign "Own It" seeing 2× engagement vs last week', meta: 'Instagram', tag: 'Instagram', tone: 'pos' },
    { text: 'Competitor A sentiment dropped -12 pts after outage', meta: 'SignalSense™', tag: 'Alert', tone: 'neu' },
    { text: 'Whitespace detected: sustainability angle unclaimed in category', meta: 'AI Insight', tag: 'Insight', tone: 'pos' },
  ],
  chip: 'SignalSense™ · competitor spike detected',
};

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function CompetitiveBenchmarkingPage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="cb-hero-orbs">
          <span className="cb-orb cb-orb1" />
          <span className="cb-orb cb-orb2" />
        </div>
        <InlineStyle css={`
          .cb-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .cb-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .cb-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:cbOrb1 22s ease-in-out infinite; }
          .cb-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:cbOrb2 26s ease-in-out infinite; }
          @keyframes cbOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes cbOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .cb-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Competitive Benchmarking')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Competitive Benchmarking')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Competitive')} <span className="serif-accent italic">{t('Benchmarking')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE STACK ──────────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('The Competitive Edge')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Competitive Edge Market Leaders')} <span className="serif-accent italic">{t('Count On')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 780, margin: '0 auto', lineHeight: 1.7 }}>{t('Great strategy is built on clarity, not assumptions. Outpace industry rivals with Real-Time Competitive Benchmarking — continuously tracking competitor share of voice, campaign performance, and audience sentiment across every digital channel.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('The Intelligence Stack')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Full-Stack Behind')} <span className="serif-accent italic">{t('Smarter Market Intelligence')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 760, margin: '0 auto', lineHeight: 1.7 }}>{t('Locobuzz transforms market observation into strategic momentum. It\'s not just about knowing what others are doing — it\'s about knowing exactly what your brand must do next.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="competitive-benchmarking-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
